import React, { useCallback, useEffect, useMemo, useState } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ScienceIcon from "@mui/icons-material/Science";
import PublicIcon from "@mui/icons-material/Public";
import { AiBreakdown } from "./components/AiBreakdown";
import { ClientBreakdown } from "./components/ClientBreakdown";
import { InfraBreakdown } from "./components/InfraBreakdown";
import { PipelineBreakdown } from "./components/PipelineBreakdown";
import { VendorBreakdown } from "./components/VendorBreakdown";
import { fetchAiAnalyticsBothEnvs, fetchOrganizationNames } from "./api";
import { formatMoney, formatNumber, formatPercent, shareOf } from "./format";
import { isInternalOrg } from "./internalOrgs";
import "../OrgUsageAnalytics/OrgUsageAnalytics.css";
import "./CostOverview.css";

const WINDOWS = [
  { value: "1h", label: "1 hour" },
  { value: "6h", label: "6 hours" },
  { value: "24h", label: "24 hours" },
  { value: "7d", label: "7 days" },
  { value: "30d", label: "30 days" },
];

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "pipelines", label: "AI pipelines & steps" },
  { id: "features", label: "AI features" },
  { id: "clients", label: "AI by client" },
  { id: "infra", label: "Infrastructure" },
  { id: "vendors", label: "Vendors" },
];

/**
 * Prod vs dev, always visible regardless of tab. The original cost audit found
 * the dev project burning production-grade tokens; that is only obvious when
 * the two numbers sit next to each other, so this strip is not collapsible.
 */
function EnvSplit({ prodCost, devCost, prodError, devError }) {
  const total = (prodCost || 0) + (devCost || 0);
  const devShare = shareOf(devCost, total);

  return (
    <div className="co-envsplit">
      <div className="co-envsplit-card co-envsplit-card--prod">
        <div className="co-envsplit-head">
          <PublicIcon fontSize="small" />
          <span>Production</span>
          <span className="co-envsplit-project">hey-addy-chatgpt</span>
        </div>
        <div className="co-envsplit-value">
          {prodError ? "—" : formatMoney(prodCost)}
        </div>
        <div className="co-envsplit-sub">
          {prodError ? "Failed to load" : "Traced AI spend in window"}
        </div>
      </div>

      <div className="co-envsplit-card co-envsplit-card--dev">
        <div className="co-envsplit-head">
          <ScienceIcon fontSize="small" />
          <span>Development</span>
          <span className="co-envsplit-project">addy-ai-dev</span>
        </div>
        <div className="co-envsplit-value">
          {devError ? "—" : formatMoney(devCost)}
        </div>
        <div className="co-envsplit-sub">
          {devError
            ? "Failed to load"
            : `${formatPercent(devShare)} of all traced AI spend`}
        </div>
      </div>

      {!devError && !prodError && devShare > 0.1 ? (
        <div className="co-envsplit-alert">
          <WarningAmberIcon fontSize="small" />
          <span>
            Dev is {formatPercent(devShare)} of traced AI spend. Non-production
            work at that share is worth a quota cap.
          </span>
        </div>
      ) : null}
    </div>
  );
}

function CostOverviewPage({ onReady }) {
  const [tab, setTab] = useState("overview");
  const [window_, setWindow] = useState("24h");
  const [env, setEnv] = useState("prod");
  const [excludeInternal, setExcludeInternal] = useState(true);

  const [loading, setLoading] = useState(true);
  const [main, setMain] = useState(null);
  const [modelMatrix, setModelMatrix] = useState(null);
  const [clientMatrix, setClientMatrix] = useState(null);
  const [orgNames, setOrgNames] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Three questions, both environments, all in flight at once:
      // 1. everything grouped by feature (also yields per-feature clients)
      // 2. feature x model
      // 3. client x feature
      const [mainRes, modelRes, clientRes] = await Promise.all([
        fetchAiAnalyticsBothEnvs({ window: window_, rowLimit: 200 }),
        fetchAiAnalyticsBothEnvs({
          window: window_,
          groupBy: "traceName",
          secondaryGroupBy: "model",
          rowLimit: 500,
        }),
        fetchAiAnalyticsBothEnvs({
          window: window_,
          groupBy: "organizationId",
          secondaryGroupBy: "traceName",
          rowLimit: 500,
        }),
      ]);

      setMain(mainRes);
      setModelMatrix(modelRes);
      setClientMatrix(clientRes);

      // Resolve org names for whichever ids showed up, in either env.
      const ids = new Set();
      ["prod", "dev"].forEach((e) => {
        (mainRes[e]?.data?.leaderboards?.organizations || []).forEach((o) => {
          if (o.key) ids.add(o.key);
        });
      });
      if (ids.size > 0) {
        // Names live in the same Firestore the env points at, so resolve
        // against prod — dev org ids that don't exist there simply won't
        // resolve and fall back to the raw id.
        const names = await fetchOrganizationNames("prod", Array.from(ids));
        setOrgNames(names);
      }
    } catch (err) {
      // fetchAiAnalyticsBothEnvs already settles per-env, so reaching here
      // means something structural failed. Surface it rather than showing an
      // empty page that looks like "no spend".
      // eslint-disable-next-line no-console
      console.error("[CostOverview] load failed", err);
      setMain({
        prod: { data: null, error: err.message },
        dev: { data: null, error: err.message },
      });
    } finally {
      setLoading(false);
      if (onReady) onReady();
    }
  }, [window_, onReady]);

  useEffect(() => {
    load();
  }, [load]);

  const prodData = main?.prod?.data;
  const devData = main?.dev?.data;
  const activeData = env === "prod" ? prodData : devData;
  const activeError = env === "prod" ? main?.prod?.error : main?.dev?.error;
  const activeModelMatrix =
    env === "prod" ? modelMatrix?.prod?.data : modelMatrix?.dev?.data;
  const activeClientMatrix =
    env === "prod" ? clientMatrix?.prod?.data : clientMatrix?.dev?.data;

  const features = useMemo(
    () =>
      Object.values(activeData?.byTraceName || {}).sort(
        (a, b) => (b.summary?.totalCost || 0) - (a.summary?.totalCost || 0),
      ),
    [activeData],
  );

  const totalCost = activeData?.summary?.totalCost || 0;

  // Internal-org spend, computed once so the overview can state coverage
  // honestly rather than just hiding rows.
  const internalSummary = useMemo(() => {
    const orgs = activeData?.leaderboards?.organizations || [];
    let internal = 0;
    let external = 0;
    let unattributed = 0;
    orgs.forEach((o) => {
      if (!o.key || o.key === "Unknown") unattributed += o.totalCost || 0;
      else if (isInternalOrg(o.key, orgNames[o.key])) internal += o.totalCost || 0;
      else external += o.totalCost || 0;
    });
    return { internal, external, unattributed };
  }, [activeData, orgNames]);

  return (
    <div className="ou-page co-page">
      <div className="co-header">
        <div className="co-header-row">
          <div>
            <h1 className="co-title">Cost Overview</h1>
            <p className="co-sub">
              Every provider we spend money on. AI spend is live from Langfuse
              and drills down to pipeline, step, model and client.
              Infrastructure and vendors are catalogued but not yet priced —
              there is no billing API access.
            </p>
          </div>
          <button
            type="button"
            className="co-refresh"
            onClick={load}
            disabled={loading}
          >
            <RefreshIcon fontSize="small" />
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>

        <div className="co-controls">
          <div className="co-control">
            <span className="co-control-label">Window</span>
            <div className="co-segmented">
              {WINDOWS.map((w) => (
                <button
                  key={w.value}
                  type="button"
                  className={window_ === w.value ? "is-active" : ""}
                  onClick={() => setWindow(w.value)}
                >
                  {w.label}
                </button>
              ))}
            </div>
          </div>

          <div className="co-control">
            <span className="co-control-label">Environment</span>
            <div className="co-segmented">
              <button
                type="button"
                className={env === "prod" ? "is-active" : ""}
                onClick={() => setEnv("prod")}
              >
                Production
              </button>
              <button
                type="button"
                className={env === "dev" ? "is-active" : ""}
                onClick={() => setEnv("dev")}
              >
                Development
              </button>
            </div>
          </div>

          <div className="co-control">
            <span className="co-control-label">Clients</span>
            <label className="co-toggle">
              <input
                type="checkbox"
                checked={excludeInternal}
                onChange={(e) => setExcludeInternal(e.target.checked)}
              />
              <span>Exclude Addy-owned orgs</span>
            </label>
          </div>
        </div>
      </div>

      <EnvSplit
        prodCost={prodData?.summary?.totalCost}
        devCost={devData?.summary?.totalCost}
        prodError={main?.prod?.error}
        devError={main?.dev?.error}
      />

      <div className="co-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            className={tab === t.id ? "is-active" : ""}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeError && tab !== "infra" && tab !== "vendors" ? (
        <div className="co-error">
          <WarningAmberIcon fontSize="small" />
          <div>
            <strong>Could not load {env} AI data.</strong>
            <pre>{activeError}</pre>
          </div>
        </div>
      ) : null}

      {loading && !activeData && tab !== "infra" && tab !== "vendors" ? (
        <p className="co-empty">Loading AI cost data…</p>
      ) : null}

      {tab === "overview" ? (
        <div className="co-section">
          <div className="ou-kpi-grid">
            <div className="ou-kpi ou-kpi--indigo">
              <div className="ou-kpi-head">
                <span className="ou-kpi-label">Traced AI spend ({env})</span>
              </div>
              <div className="ou-kpi-value">{formatMoney(totalCost)}</div>
              <div className="ou-kpi-sub">
                {formatNumber(activeData?.summary?.traceCount)} runs in window
              </div>
            </div>
            <div className="ou-kpi ou-kpi--green">
              <div className="ou-kpi-head">
                <span className="ou-kpi-label">Customer spend</span>
              </div>
              <div className="ou-kpi-value">
                {formatMoney(internalSummary.external)}
              </div>
              <div className="ou-kpi-sub">
                {formatPercent(shareOf(internalSummary.external, totalCost))} of
                traced spend
              </div>
            </div>
            <div className="ou-kpi ou-kpi--amber">
              <div className="ou-kpi-head">
                <span className="ou-kpi-label">Addy-internal spend</span>
              </div>
              <div className="ou-kpi-value">
                {formatMoney(internalSummary.internal)}
              </div>
              <div className="ou-kpi-sub">
                Name/ID heuristic only — a floor, not exact
              </div>
            </div>
            <div className="ou-kpi ou-kpi--rose">
              <div className="ou-kpi-head">
                <span className="ou-kpi-label">Unattributed</span>
              </div>
              <div className="ou-kpi-value">
                {formatMoney(internalSummary.unattributed)}
              </div>
              <div className="ou-kpi-sub">
                Traces with no org: tag — real spend we cannot bill back
              </div>
            </div>
            <div className="ou-kpi ou-kpi--cyan">
              <div className="ou-kpi-head">
                <span className="ou-kpi-label">Features seen</span>
              </div>
              <div className="ou-kpi-value">{formatNumber(features.length)}</div>
              <div className="ou-kpi-sub">Distinct trace names in window</div>
            </div>
            <div className="ou-kpi ou-kpi--pink">
              <div className="ou-kpi-head">
                <span className="ou-kpi-label">Clients active</span>
              </div>
              <div className="ou-kpi-value">
                {formatNumber(activeData?.summary?.uniqueOrganizations)}
              </div>
              <div className="ou-kpi-sub">Orgs with at least one AI run</div>
            </div>
          </div>

          <div className="co-disclaimer" style={{ marginTop: 20 }}>
            <WarningAmberIcon fontSize="small" />
            <span>
              These numbers cover <strong>Langfuse-traced AI calls only</strong>
              . They exclude the Vertex Provisioned Throughput commitment, all
              GCP infrastructure, and every non-AI vendor — and Langfuse itself
              understates Gemini cost due to a model-alias pricing bug. Treat
              this as the traced floor, not the invoice. See the Infrastructure
              tab for what it would take to close the gap.
            </span>
          </div>
        </div>
      ) : null}

      {tab === "pipelines" ? (
        <div className="co-section">
          <h2>Pipelines and steps</h2>
          <p className="co-sub" style={{ marginBottom: 16 }}>
            Every pipeline step is its own Langfuse trace, so this is real
            step-level cost. Retries and provider fallbacks are separated out —
            that spend is work already paid for once.
          </p>
          <PipelineBreakdown features={features} />
        </div>
      ) : null}

      {tab === "features" ? (
        <div className="co-section">
          <h2>Features</h2>
          <p className="co-sub" style={{ marginBottom: 16 }}>
            Flat view of every trace name. Expand a row for its clients, models,
            loans and trend.
          </p>
          <AiBreakdown
            data={activeData}
            modelMatrix={activeModelMatrix?.breakdowns}
            orgNames={orgNames}
            excludeInternal={excludeInternal}
            envLabel={env}
          />
        </div>
      ) : null}

      {tab === "clients" ? (
        <div className="co-section">
          <h2>AI spend by client</h2>
          <p className="co-sub" style={{ marginBottom: 16 }}>
            Who costs the most, and — on expand — why: their feature mix and how
            their per-run economics compare to the platform average.
          </p>
          <ClientBreakdown
            organizations={activeData?.leaderboards?.organizations}
            featureMatrix={activeClientMatrix?.breakdowns}
            orgNames={orgNames}
            excludeInternal={excludeInternal}
            totalCost={totalCost}
          />
        </div>
      ) : null}

      {tab === "infra" ? <InfraBreakdown /> : null}

      {tab === "vendors" ? <VendorBreakdown /> : null}
    </div>
  );
}

export default CostOverviewPage;
